import type {
  CanonicalStoryboardAssetReference,
  CanonicalStoryboardDialogue,
  CanonicalStoryboardItem,
  CanonicalUnknownObject,
  StoryboardCanonicalPayload,
  StoryboardC1Status,
  StoryboardDraftIntegrity,
  StoryboardDraftResult,
  StoryboardDurationCheck,
  StoryboardQuantityConstraint,
  StoryboardReviewProjection,
  StoryboardReviewRoute,
  StoryboardRiskSignal,
  StoryboardStructureProjection,
  StoryboardStructureScene,
  StoryboardStructureShot,
} from './types';
import { parseCanonicalPayloadBase } from './payload-base';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function requireRecord(value: unknown, path: string): Record<string, unknown> {
  if (!isRecord(value)) throw new Error(`AI Session ${path} 必须是对象`);
  return value;
}

function requireString(value: unknown, path: string): string {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`AI Session ${path} 必须是非空字符串`);
  }
  return value;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function optionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function extensionsOf(
  record: Record<string, unknown>,
  declaredKeys: readonly string[]
): CanonicalUnknownObject | undefined {
  const declared = new Set(declaredKeys);
  const entries = Object.entries(record).filter(([key]) => !declared.has(key));
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

const DIALOGUE_KEYS = [
  'content',
  'dialogueType',
  'characterName',
  'emotion',
  'sequenceOrder',
] as const;

function parseDialogue(value: unknown, path: string): CanonicalStoryboardDialogue {
  const record = requireRecord(value, path);
  const extensions = extensionsOf(record, DIALOGUE_KEYS);
  return {
    content: requireString(record.content, `${path}.content`),
    ...(optionalNumber(record.dialogueType) !== undefined
      ? { dialogueType: optionalNumber(record.dialogueType) }
      : {}),
    ...(optionalString(record.characterName)
      ? { characterName: optionalString(record.characterName) }
      : {}),
    ...(optionalString(record.emotion) ? { emotion: optionalString(record.emotion) } : {}),
    ...(optionalNumber(record.sequenceOrder) !== undefined
      ? { sequenceOrder: optionalNumber(record.sequenceOrder) }
      : {}),
    ...(extensions ? { extensions } : {}),
  };
}

const REFERENCE_KEYS = ['name', 'draftItemId', 'variantDraftId', 'tagLabel'] as const;

function parseReference(value: unknown, path: string): CanonicalStoryboardAssetReference {
  if (typeof value === 'string' && value.trim()) return value;
  const record = requireRecord(value, path);
  const extensions = extensionsOf(record, REFERENCE_KEYS);
  return {
    name: requireString(record.name, `${path}.name`),
    ...(optionalString(record.draftItemId)
      ? { draftItemId: optionalString(record.draftItemId) }
      : {}),
    ...(optionalString(record.variantDraftId)
      ? { variantDraftId: optionalString(record.variantDraftId) }
      : {}),
    ...(optionalString(record.tagLabel) ? { tagLabel: optionalString(record.tagLabel) } : {}),
    ...(extensions ? { extensions } : {}),
  };
}

const ITEM_KEYS = [
  'draftItemId',
  'title',
  'shotNumber',
  'sceneId',
  'sceneNumber',
  'sceneTitle',
  'sceneOrder',
  'description',
  'shootingDescription',
  'shotSize',
  'cameraMovement',
  'estimatedDuration',
  'dialogues',
  'characters',
  'locations',
  'props',
  'metaData',
] as const;

function requireArray(value: unknown, path: string): readonly unknown[] {
  if (!Array.isArray(value)) throw new Error(`AI Session ${path} 必须是数组`);
  return value;
}

function parseItem(value: unknown, path: string): CanonicalStoryboardItem {
  const record = requireRecord(value, path);
  const estimatedDuration = optionalNumber(record.estimatedDuration);
  if (estimatedDuration === undefined || estimatedDuration <= 0) {
    throw new Error(`AI Session ${path}.estimatedDuration 必须是正数`);
  }
  const extensions = extensionsOf(record, ITEM_KEYS);
  return {
    title: requireString(record.title, `${path}.title`),
    ...(optionalNumber(record.shotNumber) !== undefined
      ? { shotNumber: optionalNumber(record.shotNumber) }
      : {}),
    ...(optionalString(record.sceneId) ? { sceneId: optionalString(record.sceneId) } : {}),
    ...(optionalString(record.sceneNumber)
      ? { sceneNumber: optionalString(record.sceneNumber) }
      : {}),
    ...(optionalString(record.sceneTitle) ? { sceneTitle: optionalString(record.sceneTitle) } : {}),
    ...(optionalNumber(record.sceneOrder) !== undefined
      ? { sceneOrder: optionalNumber(record.sceneOrder) }
      : {}),
    description: requireString(record.description, `${path}.description`),
    ...(typeof record.draftItemId === 'string' ? { draftItemId: record.draftItemId } : {}),
    shootingDescription: requireString(record.shootingDescription, `${path}.shootingDescription`),
    shotSize: requireString(record.shotSize, `${path}.shotSize`),
    cameraMovement: requireString(record.cameraMovement, `${path}.cameraMovement`),
    estimatedDuration,
    dialogues: requireArray(record.dialogues ?? [], `${path}.dialogues`).map((item, index) =>
      parseDialogue(item, `${path}.dialogues[${index}]`)
    ),
    characters: requireArray(record.characters ?? [], `${path}.characters`).map((item, index) =>
      parseReference(item, `${path}.characters[${index}]`)
    ),
    locations: requireArray(record.locations ?? [], `${path}.locations`).map((item, index) =>
      parseReference(item, `${path}.locations[${index}]`)
    ),
    props: requireArray(record.props ?? [], `${path}.props`).map((item, index) =>
      parseReference(item, `${path}.props[${index}]`)
    ),
    metaData:
      record.metaData === undefined ? {} : requireRecord(record.metaData, `${path}.metaData`),
    ...(extensions ? { extensions } : {}),
  };
}

function parseStoryboardResult(value: unknown): StoryboardDraftResult {
  const record = requireRecord(value, 'storyboardResult');
  const storyboards = requireArray(record.storyboards, 'storyboardResult.storyboards');
  const extensions = extensionsOf(record, ['storyboards']);
  return {
    storyboards: storyboards.map((item, index) =>
      parseItem(item, `storyboardResult.storyboards[${index}]`)
    ),
    ...(extensions ? { extensions } : {}),
  };
}

function parseIntegrity(value: unknown): StoryboardDraftIntegrity {
  const record = requireRecord(value, 'storyboardDraftIntegrity');
  if (typeof record.hasUnassociatedScene !== 'boolean') {
    throw new Error('AI Session storyboardDraftIntegrity.hasUnassociatedScene 必须是布尔值');
  }
  for (const field of ['unassociatedCount', 'storyboardCount'] as const) {
    if (typeof record[field] !== 'number' || !Number.isFinite(record[field])) {
      throw new Error(`AI Session storyboardDraftIntegrity.${field} 必须是有限数字`);
    }
  }
  return {
    hasUnassociatedScene: record.hasUnassociatedScene,
    unassociatedCount: record.unassociatedCount as number,
    storyboardCount: record.storyboardCount as number,
  };
}

function stringList(value: unknown, path: string): readonly string[] {
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'string')) {
    throw new Error(`AI Session ${path} 必须是字符串数组`);
  }
  return value;
}

function numberList(value: unknown, path: string): readonly number[] {
  if (
    !Array.isArray(value) ||
    value.some((item) => typeof item !== 'number' || !Number.isFinite(item))
  ) {
    throw new Error(`AI Session ${path} 必须是有限数字数组`);
  }
  return value;
}

const REVIEW_ROUTES = new Set<StoryboardReviewRoute>(['fast_track', 'selective_c1', 'full_c1']);
const C1_STATUSES = new Set<StoryboardC1Status>([
  'not_required',
  'pending',
  'waiting_user',
  'confirmed',
  'skipped',
]);

function requireNumber(value: unknown, path: string): number {
  const parsed = optionalNumber(value);
  if (parsed === undefined) throw new Error(`AI Session ${path} 必须是有限数字`);
  return parsed;
}

function parseReviewRoute(value: unknown, path: string): StoryboardReviewRoute {
  if (typeof value !== 'string' || !REVIEW_ROUTES.has(value as StoryboardReviewRoute)) {
    throw new Error(`AI Session ${path} 不是有效审核路由`);
  }
  return value as StoryboardReviewRoute;
}

function parseC1Status(value: unknown, path: string): StoryboardC1Status {
  if (typeof value !== 'string' || !C1_STATUSES.has(value as StoryboardC1Status)) {
    throw new Error(`AI Session ${path} 不是有效镜头规划状态`);
  }
  return value as StoryboardC1Status;
}

function parseRiskSignal(value: unknown, path: string): StoryboardRiskSignal {
  const record = requireRecord(value, path);
  return {
    code: requireString(record.code, `${path}.code`),
    score: requireNumber(record.score, `${path}.score`),
    ...(optionalString(record.sceneId) ? { sceneId: optionalString(record.sceneId) } : {}),
    ...(optionalString(record.detail) ? { detail: optionalString(record.detail) } : {}),
  };
}

function parseStoryboardReview(value: unknown): StoryboardReviewProjection {
  const record = requireRecord(value, 'storyboardReview');
  const sceneScoresRecord = requireRecord(record.sceneScores ?? {}, 'storyboardReview.sceneScores');
  const sceneScores = Object.fromEntries(
    Object.entries(sceneScoresRecord).map(([key, score]) => [
      key,
      requireNumber(score, `storyboardReview.sceneScores.${key}`),
    ])
  );
  const c1Version = optionalNumber(record.c1Version);
  return {
    route: parseReviewRoute(record.route, 'storyboardReview.route'),
    projectScore: requireNumber(record.projectScore, 'storyboardReview.projectScore'),
    sceneScores,
    reviewSceneIds: stringList(record.reviewSceneIds ?? [], 'storyboardReview.reviewSceneIds'),
    signals: requireArray(record.signals ?? [], 'storyboardReview.signals').map((item, index) =>
      parseRiskSignal(item, `storyboardReview.signals[${index}]`)
    ),
    policyVersion: requireNumber(record.policyVersion, 'storyboardReview.policyVersion'),
    assessmentVersion: requireNumber(
      record.assessmentVersion,
      'storyboardReview.assessmentVersion'
    ),
    c1Status: parseC1Status(record.c1Status, 'storyboardReview.c1Status'),
    ...(c1Version !== undefined ? { c1Version } : {}),
    ...(record.c1SceneIds !== undefined
      ? { c1SceneIds: stringList(record.c1SceneIds, 'storyboardReview.c1SceneIds') }
      : {}),
    ...(typeof record.riskAcknowledged === 'boolean'
      ? { riskAcknowledged: record.riskAcknowledged }
      : {}),
    ...(optionalString(record.c1ResolvedAt)
      ? { c1ResolvedAt: optionalString(record.c1ResolvedAt) }
      : {}),
  };
}

function parseStructureShot(value: unknown, path: string): StoryboardStructureShot {
  const record = requireRecord(value, path);
  return {
    order: requireNumber(record.order, `${path}.order`),
    purpose: requireString(record.purpose, `${path}.purpose`),
    shotSize: requireString(record.shotSize, `${path}.shotSize`),
    cameraMovement: requireString(record.cameraMovement, `${path}.cameraMovement`),
    assetAnchors: stringList(record.assetAnchors ?? [], `${path}.assetAnchors`),
  };
}

function parseStructureScene(value: unknown, path: string): StoryboardStructureScene {
  const record = requireRecord(value, path);
  return {
    sceneId: requireString(record.sceneId, `${path}.sceneId`),
    sceneNumber: requireString(record.sceneNumber, `${path}.sceneNumber`),
    sceneOrder: requireNumber(record.sceneOrder, `${path}.sceneOrder`),
    objective: requireString(record.objective, `${path}.objective`),
    targetDuration: requireNumber(record.targetDuration, `${path}.targetDuration`),
    shotCount: requireNumber(record.shotCount, `${path}.shotCount`),
    shots: requireArray(record.shots, `${path}.shots`).map((item, index) =>
      parseStructureShot(item, `${path}.shots[${index}]`)
    ),
  };
}

function parseStoryboardStructure(value: unknown): StoryboardStructureProjection {
  const record = requireRecord(value, 'storyboardStructure');
  return {
    route: parseReviewRoute(record.route, 'storyboardStructure.route'),
    assessmentVersion: requireNumber(
      record.assessmentVersion,
      'storyboardStructure.assessmentVersion'
    ),
    version: requireNumber(record.version, 'storyboardStructure.version'),
    status: parseC1Status(record.status, 'storyboardStructure.status'),
    scenes: requireArray(record.scenes, 'storyboardStructure.scenes').map((item, index) =>
      parseStructureScene(item, `storyboardStructure.scenes[${index}]`)
    ),
    ...(typeof record.riskAcknowledged === 'boolean'
      ? { riskAcknowledged: record.riskAcknowledged }
      : {}),
    ...(optionalString(record.resolvedAt) ? { resolvedAt: optionalString(record.resolvedAt) } : {}),
  };
}

function parseDurationCheck(value: unknown): StoryboardDurationCheck | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const { minimumSeconds, maximumSeconds, dialogueSeconds, toleranceSeconds } = record;
  if (typeof minimumSeconds !== 'number' || typeof maximumSeconds !== 'number') return null;
  return {
    minimumSeconds,
    maximumSeconds,
    ...(typeof toleranceSeconds === 'number' &&
    Number.isFinite(toleranceSeconds) &&
    toleranceSeconds >= 0
      ? { toleranceSeconds }
      : {}),
    ...(typeof dialogueSeconds === 'number' ? { dialogueSeconds } : {}),
  };
}

function parseQuantityConstraint(value: unknown): StoryboardQuantityConstraint | null {
  if (!isRecord(value)) return null;
  const { target, minimum, maximum, mode } = value;
  const validCount = (number: unknown): number is number =>
    typeof number === 'number' && Number.isInteger(number) && number > 0 && number <= 1000;
  if (!validCount(target)) return null;
  if (mode === 'approximate' && minimum === null && maximum === null) {
    return { target, minimum, maximum, mode };
  }
  if (
    (mode === 'exact' || mode === 'range') &&
    validCount(minimum) &&
    validCount(maximum) &&
    minimum <= target &&
    target <= maximum &&
    (mode !== 'exact' || minimum === maximum)
  )
    return { target, minimum, maximum, mode };
  return null;
}

export function parseStoryboardCanonicalPayload(
  payload: Record<string, unknown>
): StoryboardCanonicalPayload {
  const durationCheck = parseDurationCheck(payload.storyboardDurationCheck);
  const quantityConstraint = parseQuantityConstraint(payload.storyboardQuantityConstraint);
  return {
    ...parseCanonicalPayloadBase(payload),
    canRestorePreviousStoryboard: payload.canRestorePreviousStoryboard === true,
    ...(payload.storyboardResult !== undefined
      ? { storyboardResult: parseStoryboardResult(payload.storyboardResult) }
      : {}),
    ...(payload.storyboardDraftIntegrity !== undefined
      ? { storyboardDraftIntegrity: parseIntegrity(payload.storyboardDraftIntegrity) }
      : {}),
    ...(typeof payload.storyboardVersion === 'number'
      ? { storyboardVersion: payload.storyboardVersion }
      : {}),
    ...(durationCheck ? { storyboardDurationCheck: durationCheck } : {}),
    ...(quantityConstraint ? { storyboardQuantityConstraint: quantityConstraint } : {}),
    ...(payload.storyboardReview !== undefined
      ? { storyboardReview: parseStoryboardReview(payload.storyboardReview) }
      : {}),
    ...(payload.storyboardStructure !== undefined
      ? { storyboardStructure: parseStoryboardStructure(payload.storyboardStructure) }
      : {}),
    ...(payload.persistedStoryboardIds !== undefined
      ? {
          persistedStoryboardIds: stringList(
            payload.persistedStoryboardIds,
            'persistedStoryboardIds'
          ),
        }
      : {}),
    ...(typeof payload.recoverable === 'boolean' ? { recoverable: payload.recoverable } : {}),
    ...(payload.missingSceneOrders !== undefined
      ? { missingSceneOrders: numberList(payload.missingSceneOrders, 'missingSceneOrders') }
      : {}),
  };
}
